import { useDraggable, useDroppable } from "@dnd-kit/core";

import { createSchematic, Schematic } from "../Schematic";
import {
  SchematicBuilderContext,
  useSchematicBuilderContext,
} from "../SchematicBuilderContext";
import { boolDataAttr } from "../util/boolDataAttr";
import { joinPackages } from "../util/join";
import { preventDrag } from "../util/preventDrag";
import { ChildMarker } from "./util/ChildMarker";
import { ChildRemove } from "./util/ChildRemove";
import { KindSelect } from "./util/KindSelect";
import { NullableInput } from "./util/NullableInput";
import { PackageNameInput } from "./util/PackageNameInput";

export const CLASS_DND_TYPE = "class-field";

export function ClassBuilder() {
  const schematicBuilderContext = useSchematicBuilderContext();
  const { useSchematic, setSchematic, draggedSchematic, disableDrop } =
    schematicBuilderContext;
  const id = useSchematic((schematic) => schematic.id);
  const packageSuffix = useSchematic((schematic) => schematic.packageSuffix);
  const fields =
    useSchematic((schematic: Schematic) => schematic.children) ?? [];
  const shouldIgnoreDrop =
    draggedSchematic != null && draggedSchematic.id === fields.at(-1)?.id;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `${id}-append`,
    data: { parentId: id, sortBefore: null, ignored: shouldIgnoreDrop },
    disabled: disableDrop || draggedSchematic?.type !== CLASS_DND_TYPE,
  });

  return (
    <div className="class-builder builder">
      <PackageNameInput />

      {fields.length === 0 && (
        <div
          className="builder-empty"
          data-over={boolDataAttr(isOver)}
          ref={setDroppableRef}
        >
          No fields.
        </div>
      )}

      {fields.length > 0 && (
        <ul className="builder-children">
          {fields.map((field, i) => (
            <SchematicBuilderContext.Provider
              key={field.id}
              value={{
                ...schematicBuilderContext,
                useSchematic: (selector) =>
                  useSchematic((schematic) => selector(schematic.children![i])),
                setSchematic: (toSet) =>
                  setSchematic((schematic) => ({
                    ...schematic,
                    children: schematic.children!.map((f) =>
                      field === f
                        ? {
                            ...f,
                            ...(typeof toSet === "function" ? toSet(f) : toSet),
                          }
                        : f,
                    ),
                  })),
                removeSchematic: () =>
                  setSchematic((schematic) => ({
                    ...schematic,
                    children: schematic.children!.filter((f) => f !== field),
                  })),
                parentPackage: joinPackages(
                  schematicBuilderContext.parentPackage,
                  packageSuffix,
                ),
                parentId: id,
                ignoreDrop:
                  draggedSchematic != null &&
                  draggedSchematic.id === fields[i - 1]?.id,
              }}
            >
              <ClassFieldBuilder />
            </SchematicBuilderContext.Provider>
          ))}

          <li
            className="builder-child-droppable"
            data-over={boolDataAttr(isOver && !shouldIgnoreDrop)}
            ref={setDroppableRef}
          />
        </ul>
      )}

      <button
        className="builder-input builder-new-child"
        type="button"
        onClick={() => {
          setSchematic((schematic) => ({
            ...schematic,
            children: [
              ...(schematic.children ?? []),
              createSchematic({ childName: "" }),
            ],
          }));
        }}
        {...preventDrag}
      >
        Add field
      </button>
    </div>
  );
}

const CHILD_NAME_PLACEHOLDER = "fieldName";

export function ClassFieldBuilder() {
  const schematicBuilderContext = useSchematicBuilderContext();
  const {
    schematicKinds,
    useSchematic,
    setSchematic,
    draggedSchematic,
    parentId,
    disableDrop,
    ignoreDrop,
  } = schematicBuilderContext;
  const id = useSchematic((schematic) => schematic.id);
  const childName = useSchematic((schematic) => schematic.childName);
  const kind = useSchematic((schematic) => schematic.kind);
  const nullable = useSchematic((schematic) => schematic.nullable);
  const collapsed = useSchematic((schematic) => schematic.collapsed);
  const Builder = schematicKinds.get(kind)?.builder;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableRef,
    isDragging,
  } = useDraggable({
    id,
    data: {
      type: CLASS_DND_TYPE,
      node: (
        <>
          {[childName, kind].filter((s) => s).join(": ")}
          {nullable ? "?" : ""}
        </>
      ),
    },
  });
  const shouldIgnoreDrop = isDragging || ignoreDrop;
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id,
    data: { parentId, sortBefore: id, ignored: shouldIgnoreDrop },
    disabled: disableDrop || draggedSchematic?.type !== CLASS_DND_TYPE,
  });

  return (
    <>
      <li
        className="builder-child-droppable"
        data-over={boolDataAttr(isOver && !shouldIgnoreDrop)}
        ref={setDroppableRef}
      />

      <li
        className="builder-child class-builder-field"
        data-dragging={boolDataAttr(isDragging)}
        {...listeners}
        {...attributes}
        ref={setDraggableRef}
        data-collapsible={boolDataAttr(Builder != null)}
      >
        <div className="builder-child-content">
          <ChildMarker />
          <input
            className="builder-input class-builder-field-id"
            placeholder={CHILD_NAME_PLACEHOLDER}
            value={childName}
            onChange={(evt) => setSchematic({ childName: evt.target.value })}
            required
            pattern="[a-zA-Z_][a-zA-Z0-9_]*"
            style={{
              width: `${(childName || CHILD_NAME_PLACEHOLDER).length}ch`,
            }}
            {...preventDrag}
          />
          <span className="builder-operator">∶</span>
          <SchematicBuilderContext.Provider
            value={{ ...schematicBuilderContext, parentChildName: childName }}
          >
            <KindSelect />
          </SchematicBuilderContext.Provider>
          <NullableInput />
          <ChildRemove />
        </div>

        {Builder && !collapsed && (
          <SchematicBuilderContext.Provider
            value={{
              ...schematicBuilderContext,
              parentChildName: childName,
              disableDrop: disableDrop || isDragging,
            }}
          >
            <Builder />
          </SchematicBuilderContext.Provider>
        )}
      </li>
    </>
  );
}
